import React, { useState, useMemo, useEffect } from "react";
import { Link, useLocation, useParams, useNavigate } from "react-router-dom";
import { FaRightFromBracket, FaGear, FaChevronLeft, FaChevronRight, FaArrowLeft } from "react-icons/fa6";
import {
  FaRobot,
  FaCogs,
  FaFlask,
  FaExclamationTriangle,
  FaUsers,
  FaBrain,
  FaComments,
  FaRegComments
} from "react-icons/fa";
import { BiChevronDown } from "react-icons/bi";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";

const TopSidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { tenantId, botId } = useParams();

  const [openDropdown, setOpenDropdown] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [companyName, setCompanyName] = useState("");

  const userRole = localStorage.getItem("role") || "member";
  const isAdmin = userRole === "admin";

  const resolvedTenantId = tenantId;
  const resolvedBotId = botId;

  const menuItems = useMemo(() => {
    if (!resolvedTenantId || !resolvedBotId) return [];

    const allowedBotLabels = [
      "Overview",
      "Live Conversations",
      "Test Assist",
      "Escalations",
      "Leads",
      "Conversations",
      // "Conversations (Admin)"
    ];

    const items = [
      {
        label: "Dashboard",
        icon: FaArrowLeft,
        path: `/dashboard/${resolvedTenantId}`,
        level: "tenant"
      },
      {
        label: "Overview",
        icon: FaRobot,
        path: `/overview/${resolvedTenantId}/${resolvedBotId}`,
        level: "bot"
      },
      {
        label: "Live Conversations",
        icon: FaComments,
        path: `/overview/${resolvedTenantId}/${resolvedBotId}/live`,
        level: "bot"
      },
      {
        label: "Configuration",
        icon: FaCogs,
        key: "configuration",
        level: "bot",
        subItems: [
          {
            label: "Agent Configuration",
            path: `/configuration/${resolvedTenantId}/${resolvedBotId}/config`
          },
          {
            label: "Recommendation system",
            path: `/configuration/${resolvedTenantId}/${resolvedBotId}/recomsystem`
          },
          {
            label: "Prompts",
            path: `/configuration/${resolvedTenantId}/${resolvedBotId}/prompts`
          }
        ]
      },
      {
        label: "Knowledge Base",
        icon: FaBrain,
        path: `/knowledgeafteronboard/${resolvedTenantId}/${resolvedBotId}`,
        level: "bot"
      },
      {
        label: "Test Assist",
        icon: FaFlask,
        path: `/test-assist/${resolvedTenantId}/${resolvedBotId}/chat`,
        level: "bot"
      },
      {
        label: "Escalations",
        icon: FaExclamationTriangle,
        path: `/leads/${resolvedTenantId}/${resolvedBotId}`,
        level: "bot"
      },
      {
        label: "Leads",
        icon: FaUsers,
        path: `/lead-users/${resolvedTenantId}/${resolvedBotId}`,
        level: "bot"
      },
      {
        label: "Knowledge Gaps",
        icon: FaBrain,
        path: `/kb-gaps/${resolvedTenantId}/${resolvedBotId}`,
        level: "bot"
      },
      {
        label: "Conversations",
        icon: FaRegComments,
        path: `/monitor/conversations/${resolvedTenantId}/${resolvedBotId}`,
        level: "bot"
      },
      // {
      //   label: "Conversations (Admin)",
      //   icon: FaRegComments,
      //   path: `/monitor/conversationsad/${resolvedTenantId}/${resolvedBotId}`,
      //   level: "bot"
      // },
      {
        label: "Settings",
        icon: FaGear,
        path: `/settings/${resolvedTenantId}/${resolvedBotId}`,
        level: "bot"
      }
    ];

    return items.filter((item) => {
      if (item.level === "tenant") return true;
      if (isAdmin) return true;
      if (item.level === "bot") return allowedBotLabels.includes(item.label);
      return false;
    });
  }, [resolvedTenantId, resolvedBotId, isAdmin]);

  useEffect(() => {
    if (tenantId) localStorage.setItem("tenant_id", tenantId);
    if (botId) localStorage.setItem("bot_id", botId);
  }, [tenantId, botId]);

  useEffect(() => {
    const name = localStorage.getItem("company_name");
    if (name && name !== "undefined" && name !== "null") {
      setCompanyName(name);
    }
  }, []);

  const MenuItem = ({ item }) => {
    const Icon = item.icon;
    const isActive =
  item.label === "Overview"
    ? location.pathname === item.path
    : item.path && location.pathname.startsWith(item.path);
    const subActive =
      item.subItems && item.subItems.some((sub) => location.pathname.startsWith(sub.path));

    const tooltipId = `tooltip-${item.label.replace(/\s+/g, "-").toLowerCase()}`;

    const handleSubItemClick = (path) => {
      navigate(path);
    };

    return (
      <div className="relative">
        <button
          {...(!sidebarOpen && {
            "data-tooltip-id": tooltipId,
            "data-tooltip-place": "right",
            ...(item.subItems ? {} : { "data-tooltip-content": item.label })
          })}
          onClick={() => {
            if (item.subItems) {
              setOpenDropdown(openDropdown === item.label ? null : item.label);
            } else if (item.path) {
              navigate(item.path);
            }
          }}
          className={`group flex items-center w-full transition-colors duration-150 border-l-2 ${
            sidebarOpen ? "px-4 py-2.5" : "p-2.5 justify-center"
          } ${
            isActive || subActive
              ? "border-l-[#B89D4F] bg-white/[0.06] text-white font-semibold"
              : "border-l-transparent text-[#D9CADD] hover:bg-white/[0.04] hover:text-white"
          }`}
        >
          <Icon className="text-base shrink-0" />

          {sidebarOpen && <span className="ml-3 text-sm truncate">{item.label}</span>}

          {item.subItems && sidebarOpen && (
            <BiChevronDown
              className={`ml-auto text-xs transition-transform ${
                openDropdown === item.label ? "rotate-180" : ""
              }`}
            />
          )}
        </button>

        <Tooltip
          id={tooltipId}
          place="right"
          positionStrategy="fixed"
          offset={20}
          clickable
          style={{
            backgroundColor: "#3D2542",
            color: "#fff",
            padding: "10px 12px",
            borderRadius: "8px",
            fontSize: "12px",
            lineHeight: "1.5",
            fontWeight: "500",
            maxWidth: "240px",
            zIndex: 99999,
            border: "1px solid rgba(255,255,255,0.08)"
          }}
          content={
            item.subItems ? (
              <div>
                <div
                  style={{
                    fontWeight: 600,
                    marginBottom: "8px",
                    borderBottom: "1px solid rgba(255,255,255,0.15)",
                    paddingBottom: "6px"
                  }}
                >
                  {item.label}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  {item.subItems.map((sub) => (
                    <div
                      key={sub.label}
                      onClick={() => handleSubItemClick(sub.path)}
                      style={{
                        padding: "6px 8px",
                        borderRadius: "4px",
                        cursor: "pointer",
                        transition: "background 0.15s"
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.08)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }}
                    >
                      {sub.label}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              item.label
            )
          }
        />

        {item.subItems && openDropdown === item.label && sidebarOpen && (
          <div className="ml-9 mt-1 space-y-0.5">
            {item.subItems.map((sub) => {
              const active = location.pathname.startsWith(sub.path);
              return (
                <Link
                  key={sub.label}
                  to={sub.path}
                  className={`relative block py-1.5 pl-3 text-[13px] rounded transition-colors ${
                    active
                      ? "text-white font-semibold bg-white/[0.06]"
                      : "text-[#D9CADD] hover:text-white hover:bg-white/[0.04]"
                  }`}
                >
                  {active && (
                    <span className="absolute left-0 top-0 h-full w-[2px] bg-[#B89D4F] rounded-full" />
                  )}
                  {sub.label}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex">
      <style>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>

      <aside
        className="fixed top-0 left-0 h-full bg-[#4A2E50] border-r border-black/10 transition-all duration-200 z-40 flex flex-col"
        style={{ width: sidebarOpen ? 240 : 72 }}
      >
        <div className="flex-shrink-0 flex items-center px-4 h-16 border-b border-white/10">
          <div className="w-8 h-8 rounded-md bg-[#B89D4F] flex items-center justify-center shrink-0">
            <span className="text-white text-sm font-bold">
              {companyName ? companyName[0].toUpperCase() : "D"}
            </span>
          </div>

          {sidebarOpen && (
            <div className="ml-3 flex flex-col justify-center overflow-hidden">
              <span className="text-white font-semibold text-sm truncate">
                {companyName || "Workspace"}
              </span>
              <span className="text-[#B9A6BC] text-[10px] uppercase tracking-wider">
                Bot Manager
              </span>
            </div>
          )}

          {sidebarOpen && (
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-2 rounded-md text-[#D9CADD] hover:text-white hover:bg-white/[0.06] transition-colors ml-auto"
              aria-label="Collapse sidebar"
            >
              <FaChevronLeft size={14} />
            </button>
          )}
        </div>

        {!sidebarOpen && (
          <div className="flex-shrink-0 flex justify-center py-2 border-b border-white/10">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-md text-[#D9CADD] hover:text-white hover:bg-white/[0.06] transition-colors"
              aria-label="Expand sidebar"
            >
              <FaChevronRight size={14} />
            </button>
          </div>
        )}

        <nav 
          className="flex-1 overflow-y-auto overflow-x-hidden space-y-0.5 mt-4 px-3 pb-2 hide-scrollbar"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {menuItems.map((item, idx) => (
            <React.Fragment key={item.label}>
              <MenuItem item={item} />
              {item.level === "tenant" && idx === 0 && (
                <div className="my-2 mx-4 border-t border-white/10" />
              )}
            </React.Fragment>
          ))}
        </nav>

        <div className="flex-shrink-0 px-3 pb-4 pt-2 border-t border-white/10">
          <button
            {...(!sidebarOpen && {
              "data-tooltip-id": "logout-tooltip",
              "data-tooltip-content": "Logout",
              "data-tooltip-place": "right"
            })}
            onClick={() => {
              localStorage.clear();
              navigate("/");
            }}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-white/15 hover:bg-white/[0.06] text-white text-sm font-medium rounded-md transition-colors"
          >
            <FaRightFromBracket size={15} />
            {sidebarOpen && "Logout"}
          </button>

          <Tooltip
            id="logout-tooltip"
            place="right"
            positionStrategy="fixed"
            offset={10}
            style={{
              backgroundColor: "#3D2542",
              color: "#fff",
              padding: "6px 10px",
              borderRadius: "6px",
              fontSize: "11px",
              zIndex: 99999
            }}
          />
        </div>
      </aside>

      <div style={{ marginLeft: sidebarOpen ? 240 : 72 }} />
    </div>
  );
};

export default TopSidebar;